import { computed, inject } from '@angular/core';
import { updateState, withDevtools, withFeatureFactory } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { setPageNumber, setPageSize, withPagination, withPaginationMethods } from '@shared/state/pagination.feature';
import { setError, setFulfilled, setPending, withRequestStatus } from '@shared/state/request-status.feature';
import { pipe, switchMap, tap } from 'rxjs';

import { FilterTrainingDataRequest } from '../models/filter-training-data-request.model';
import { TrainingTextItem } from '../models/training-text-item.model';
import { AiKnowledgeService } from '../service/ai-knowledge.service';

export const AiKnowledgeStore = signalStore(
  withDevtools('ai-knowledge'),
  withState({
    data: null as TrainingTextItem[] | null,
  }),
  withRequestStatus(),
  withFeatureFactory((store) => withPagination<TrainingTextItem>(store.data)),
  withComputed(({ data }) => ({
    trainingData: computed(() => data()),
  })),
  withMethods((store, aiKnowledgeService = inject(AiKnowledgeService)) => ({
    load: rxMethod<FilterTrainingDataRequest>(
      pipe(
        tap(({ pageNumber, pageSize, enableGlobalLoader }) => {
          if (!enableGlobalLoader) {
            updateState(store, '[AiKnowledgeStore] Set pending', setPending());
          }
          updateState(store, '[AiKnowledgeStore] Set page number', setPageNumber(pageNumber));
          updateState(store, '[AiKnowledgeStore] Set page size', setPageSize(pageSize));
        }),
        switchMap(({ enableGlobalLoader }) =>
          aiKnowledgeService
            .getTrainingData({ pageSize: store.pageSize(), pageNumber: store.pageNumber(), enableGlobalLoader })
            .pipe(
              tapResponse({
                next: (data) => {
                  updateState(store, '[AiKnowledgeStore] Set fulfilled', setFulfilled());
                  updateState(store, '[AiKnowledgeStore] Set data', { data });
                },
                error: () => {
                  updateState(store, '[AiKnowledgeStore] Set error', setError());
                  updateState(store, '[AiKnowledgeStore] Set data', { data: null });
                },
              }),
            ),
        ),
      ),
    ),
  })),
  withFeatureFactory((store) => withPaginationMethods(store)),
);
